/**
 * Which language the app opens in.
 *
 * The account's agreed comms language is the authority; the browser is a
 * per-user cache in front of it, not a competing source.
 *
 * Before this, the app read one shared `locale` key and nothing else — it
 * never looked at the signed-in person's language at all. A worker recorded
 * as speaking only Russian got an Estonian interface and had to find the
 * switcher, and on a shared site phone the next person inherited whatever
 * the previous one had picked. Keying the cache by uid fixes the second
 * problem; reading the account fixes the first.
 */

const SUPPORTED = ['et', 'en', 'ru', 'fi'] as const;
export type AppLocale = (typeof SUPPORTED)[number];

export function isAppLocale(v: unknown): v is AppLocale {
  return typeof v === 'string' && (SUPPORTED as readonly string[]).includes(v);
}

/** Per-user key, so a shared device does not leak one person's language to the next. */
export function localeCacheKey(uid: string): string {
  return `locale:${uid}`;
}

/**
 * The pre-auth boot key. Read before anyone is signed in; never written from
 * an account-derived value, or it would launder the account default into
 * something that looks like a person's own choice.
 */
const BOOT_KEY = 'locale';

export const DEFAULT_LOCALE: AppLocale = 'et';

/** The locale to start the app in before we know who is signing in. */
export function bootLocale(): AppLocale {
  try {
    const v = localStorage.getItem(BOOT_KEY);
    return isAppLocale(v) ? v : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Record a language the PERSON chose, from the switcher. Only an explicit
 * choice is ever written here — see resolveLocaleOnSignIn for why.
 */
export function writeChosenLocale(uid: string | null, locale: AppLocale): void {
  try {
    if (uid) localStorage.setItem(localeCacheKey(uid), locale);
    localStorage.setItem(BOOT_KEY, locale);
  } catch {
    /* storage unavailable — the account value still applies next sign-in */
  }
}

/** Forget a device choice, so the account language governs again. */
export function clearChosenLocale(uid: string): void {
  try {
    localStorage.removeItem(localeCacheKey(uid));
  } catch {
    /* nothing to do */
  }
}

/**
 * The language to switch to once we know who is signed in.
 *
 * Precedence, and the reasoning for each step:
 *
 *   1. A language THIS person chose on THIS device, from the switcher. More
 *      recent information than an account default, and overriding it would
 *      fight the switcher.
 *   2. The language agreed on their account.
 *   3. The app default.
 *
 * Step 3 is not "leave it as it is". On a shared site phone, leaving it would
 * hand the previous worker's language to the next one — the very thing the
 * per-user key exists to prevent. An unagreed worker gets the app default,
 * and the admin screen says out loud that nobody has agreed one.
 *
 * An ANONYMOUS session (the /order guest) is not an account: it has no agreed
 * language and no shared-phone history to protect, and the language on screen
 * is the one the guest chose (boot key / switcher). It is left alone — null.
 * Resetting it to the default turned every guest's English page Estonian
 * the moment the order form signed them in (D1).
 *
 * Nothing here writes to storage. An account-derived language must NOT be
 * cached as though the person picked it: cached, it would outrank the account
 * on the next sign-in, and an admin correcting the language would never reach
 * that device.
 */
export function resolveLocaleOnSignIn(args: {
  uid: string;
  accountLocale: string | null | undefined;
  current: string;
  /** Anonymous guest session: never changes the language. */
  isAnonymous?: boolean;
}): AppLocale | null {
  if (args.isAnonymous) return null;
  let next: AppLocale = DEFAULT_LOCALE;
  const chosen = (() => {
    try {
      const v = localStorage.getItem(localeCacheKey(args.uid));
      return isAppLocale(v) ? v : null;
    } catch {
      return null;
    }
  })();
  if (chosen) next = chosen;
  else if (isAppLocale(args.accountLocale)) next = args.accountLocale;

  return next === args.current ? null : next;
}
